import { apiClientV2, unwrapV2Response } from './index.js';

/**
 * v2 Billing API. Uses the v2 envelope { success, message, data, errors }.
 * Follows the v2 user call pattern (getUserData in users.js), and also keeps
 * the backend `message` and `errors[0]` code on failures.
 * Products, billing portal and subscription status stay on v1 (stripe.js / subscriptions.js).
 */

/**
 * Turns an HTTPClient error (or a thrown error) into an Error that carries the
 * backend message and code.
 *
 * HTTPClient returns the v2 envelope as `error` for EXPECTED 4xx responses, but
 * nests it under `originalError` once retries are exhausted (401 refresh path,
 * 5xx, network). A rejected call (e.g. `refreshAuth` throwing) arrives as a plain Error.
 *
 * @param {object} error HTTPClient `error` value, a v2 envelope, a retry wrapper or an Error
 * @param {string} fallback Message used when neither the envelope nor the wrapper has one
 * @returns {Error} with `.code` = backend errors[0] or null, and `.fromBackend` = true only
 *   when `.message` came from a v2 envelope (not a transport/client string such as
 *   'Max retries exceeded', 'Network connectivity error' or 'Failed to fetch')
 */
const toBillingError = (error, fallback) => {
	const envelope = error?.originalError ?? error;
	// Only a v2 envelope carries a message written by the backend; anything else is a
	// transport/client string ('Max retries exceeded', 'Network connectivity error', …).
	const fromBackend = envelope?.success === false && !!envelope?.message;
	const err = new Error(envelope?.message || error?.message || fallback);
	err.code = envelope?.errors?.[0] ?? null;
	err.fromBackend = fromBackend;
	return err;
};

/**
 * Starts a Stripe checkout session (POST /v2/billing/checkout).
 *
 * `mode` defaults to 'payment' (as the v1 wrapper did) and is always sent, so
 * one-time products are not switched to the server default 'subscription'.
 *
 * Every failure is normalised the same way: an `error` returned by HTTPClient, a
 * rejected request (e.g. the token refresh throwing) and a 200 `success: false` envelope.
 *
 * @param {{ price_id: string, mode?: string, return_path?: string }} params
 * @returns {Promise<{ checkout_url: string }>}
 * @throws {Error} with `.code` = backend errors[0] (e.g. 'account_not_verified') or null,
 *   and `.fromBackend` = true when `.message` is the backend envelope message
 */
export const createCheckoutV2 = async ({ price_id, mode = 'payment', return_path = '' }) => {
	let response;
	try {
		response = await apiClientV2.post('/billing/checkout', {
			origin: window.location.origin,
			price_id,
			mode,
			return_path,
		});
	} catch (thrown) {
		throw toBillingError(thrown, 'Could not start checkout');
	}
	const { data: apiResponse, error } = response;
	if (error) throw toBillingError(error, 'Could not start checkout');
	try {
		return unwrapV2Response(apiResponse);
	} catch (err) {
		err.code = err.apiErrors?.[0] ?? null;
		err.fromBackend = !!apiResponse?.message;
		throw err;
	}
};
